import { useCallback, useEffect, useRef, useState } from 'react'

import { readHeard, rememberHeard } from '../lib/heardPosts'
import { fetchPostAudio } from '../services/social'
import { useAuthStore } from '../store/authStore'

/**
 * One player for the feed: which card owns it, where it is, and whether it
 * is fetching — the archive's player (`useArchivePlayer`) over the feed's
 * bearer-gated recordings (`social_audio`, B6). Each recording is pulled
 * through the authenticated client once, kept as an object URL for the
 * page's lifetime, and revoked on unmount.
 */
export function useFeedPlayer() {
  const audioRef = useRef(null)
  const urlsRef = useRef(new Map())
  const [playingId, setPlayingId] = useState(null)
  const [loadingId, setLoadingId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)
  // Posts whose recording has played to the end for this learner (owner rule
  // 2026-10-14, «تا آخر پخش کرده»): clap and rate open only then. Kept on the
  // device per learner (lib/heardPosts.js), so a reload does not ask again;
  // the server checks its own side (`listen_first`).
  const userId = useAuthStore((s) => s.user?.id ?? null)
  const userIdRef = useRef(userId)
  userIdRef.current = userId
  const [heard, setHeard] = useState(() => readHeard(userId))
  useEffect(() => {
    setHeard(readHeard(userId))
  }, [userId])
  const markHeard = useCallback((postId) => {
    setHeard(rememberHeard(userIdRef.current, postId))
  }, [])
  // The post the element is playing, for its `ended` event.
  const currentRef = useRef(null)

  const element = useCallback(() => {
    if (audioRef.current) return audioRef.current
    const audio = new Audio()
    audio.preload = 'auto'
    audio.addEventListener('play', () => setIsPlaying(true))
    audio.addEventListener('pause', () => setIsPlaying(false))
    audio.addEventListener('ended', () => {
      setIsPlaying(false)
      setProgress(1)
      if (currentRef.current) markHeard(currentRef.current)
    })
    audio.addEventListener('timeupdate', () => {
      if (audio.duration > 0) setProgress(Math.min(1, audio.currentTime / audio.duration))
    })
    audio.addEventListener('error', () => {
      setIsPlaying(false)
      setPlayingId(null)
      setError('failed')
    })
    audioRef.current = audio
    return audio
  }, [markHeard])

  useEffect(() => {
    const urls = urlsRef.current
    return () => {
      audioRef.current?.pause()
      urls.forEach((url) => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  const toggle = useCallback(
    async (post) => {
      const audio = element()
      if (playingId === post.id) {
        if (audio.paused) {
          await audio.play().catch(() => setError('failed'))
        } else audio.pause()
        return
      }
      if (loadingId) return

      setLoadingId(post.id)
      setError(null)
      try {
        let url = urlsRef.current.get(post.id)
        if (!url) {
          url = await fetchPostAudio(post.audioUrl)
          urlsRef.current.set(post.id, url)
        }
        audio.pause()
        audio.src = url
        currentRef.current = post.id
        setProgress(0)
        setPlayingId(post.id)
        await audio.play()
      } catch {
        setPlayingId(null)
        setError('failed')
      } finally {
        setLoadingId(null)
      }
    },
    [element, loadingId, playingId],
  )

  const clearError = useCallback(() => setError(null), [])

  const hasHeard = useCallback((postId) => heard.has(postId), [heard])

  return { playingId, loadingId, isPlaying, progress, error, toggle, clearError, hasHeard }
}
